/**
 * Who is signed in, for the whole application.
 *
 * A context rather than a plain hook, because the answer gates rendering: the shell, the
 * route guard and the sign-out button all need the *same* answer at the same time, and
 * three independent `useQuery` calls would give three loading states and three chances to
 * disagree about whether the user is signed in.
 *
 * The session itself is an httpOnly cookie. Nothing here stores, reads or forwards a token
 * — this state is only the server's answer to "who am I", cached.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authApi } from '@/lib/api/auth'
import { onUnauthorized } from '@/lib/api/client'
import { queryKeys } from '@/lib/queryClient'

import type { components } from '@/types/generated/api'

export type CurrentUser = components['schemas']['CurrentUserResponse']

interface AuthState {
  user: CurrentUser | null
  isLoading: boolean
  /** True only once the server has answered. `!isAuthenticated` while loading means nothing. */
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<CurrentUser>
  signUp: (email: string, password: string, displayName?: string) => Promise<CurrentUser>
  signOut: () => Promise<void>
  refresh: () => Promise<unknown>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: authApi.session,
    // The session is the one thing worth re-checking when someone returns to the tab: it
    // may have been revoked from another device while they were away.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    retry: false,
  })

  // A 401 from anywhere means the session this tab was using is gone. Dropping the cached
  // user swaps the whole app back to the sign-in screen in one step, instead of leaving
  // every panel to fail on its own and look like a backend outage.
  useEffect(
    () =>
      onUnauthorized(() => {
        queryClient.setQueryData(queryKeys.session, null)
      }),
    [queryClient],
  )

  const logIn = useMutation({ mutationFn: authApi.logIn })
  const signUpMutation = useMutation({ mutationFn: authApi.signUp })
  const logOut = useMutation({ mutationFn: authApi.logOut })

  const adopt = useCallback(
    (user: CurrentUser) => {
      queryClient.setQueryData(queryKeys.session, user)
      return user
    },
    [queryClient],
  )

  const value = useMemo<AuthState>(
    () => ({
      user: session.data ?? null,
      isLoading: session.isLoading,
      isAuthenticated: Boolean(session.data),
      signIn: async (email, password) => adopt(await logIn.mutateAsync({ email, password })),
      signUp: async (email, password, displayName) =>
        adopt(
          await signUpMutation.mutateAsync({
            email,
            password,
            display_name: displayName || null,
          }),
        ),
      signOut: async () => {
        await logOut.mutateAsync()
        // Clear *everything*, not just the session: repositories, executions and metrics in
        // the cache all belong to the person signing out, and the next person to sign in on
        // this browser must not see a frame of them.
        queryClient.clear()
      },
      refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.session }),
    }),
    [adopt, logIn, logOut, queryClient, session.data, session.isLoading, signUpMutation],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext)
  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.')
  }
  return value
}
